//External
import React, { useEffect, useRef, useState } from "react";
import { format, parse } from "date-fns";
import { useSelector } from "react-redux";
import { useRouter } from "next/router";
//Internal
import DateRangePicker from "../date-range";
import IconArrowRight from "../icons/icons-arrow-right";
import IconCalendar from "../icons/icons-calendar";
import IconLocationPin from "../icons/icons-location-pin";
import LocationPopover, { LOCATION_PLACEHOLDER, getPickupAddress } from "../location-popover";
import { get_cookie, set_cookie } from "utils";
import { getBookableTimeOptions, pickBookableTime } from "utils/reservation";

// Time isn't surfaced in the collapsed mobile card, but it is editable
// inside the calendar sheet (mobileSheet mode on DateRangePicker) - starts
// defaulted to 10:00 same as the desktop bar.
const DEFAULT_TIME = "10:00";

/*******************
@purpose : "Apr 18, 2026" -> "Sat, Apr 18" for display only
@Parameter : {value}
@Author : INIC
******************/
const formatDisplayDate = (value) => {
  if (!value) return value;
  try {
    return format(parse(value, "MMM d, yyyy", new Date()), "EEE, MMM d");
  } catch (e) {
    return value;
  }
};

/*******************
@purpose : "14:00" -> "2:00 PM" for display only
@Parameter : {value}
@Author : INIC
******************/
const formatDisplayTime = (value) => {
  if (!value) return value;
  try {
    return format(parse(value, "H:mm", new Date()), "h:mm a");
  } catch (e) {
    return value;
  }
};

/*******************
@Purpose : Mobile-only homepage hero search card (combined start/end date
           row, location row, Next button). Kept separate from
           ReservationSearchBar - its own `.mobile-reservation-search-bar`
           CSS class/file, not the desktop pill's - so styling/behavior
           changes here can never affect the desktop search bar.
@Author : INIC
******************/
function MobileReservationSearchBar() {
  const router = useRouter();
  const currentLocationId =
    useSelector((state) => state?.common?.currentLocationId) ||
    get_cookie("currentLocationId");

  const stateObj = [
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ];

  const [dateObj, setDateObj] = useState(stateObj);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [startTime, setStartTime] = useState(DEFAULT_TIME);
  const [endTime, setEndTime] = useState(DEFAULT_TIME);

  // Never keep a time that is already in the past (or inside the booking
  // lead time) for the committed dates.
  const startTimeOptionsKey = getBookableTimeOptions(startDate)
    .map((option) => `${option.id}${option.disabled ? "x" : ""}`)
    .join(",");
  const endTimeOptionsKey = getBookableTimeOptions(
    endDate,
    startDate && startDate === endDate ? parseInt(startTime, 10) : null
  )
    .map((option) => `${option.id}${option.disabled ? "x" : ""}`)
    .join(",");

  useEffect(() => {
    const next = pickBookableTime(getBookableTimeOptions(startDate), startTime);
    if (next !== startTime) setStartTime(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTimeOptionsKey, startTime]);

  useEffect(() => {
    const next = pickBookableTime(
      getBookableTimeOptions(
        endDate,
        startDate && startDate === endDate ? parseInt(startTime, 10) : null
      ),
      endTime
    );
    if (next !== endTime) setEndTime(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endTimeOptionsKey, endTime]);
  // Only one of the two popovers (calendar, location) can be open at once.
  const [activePopover, setActivePopover] = useState(null);
  const isShowCalendar = activePopover === "calendar";
  const isLocationOpen = activePopover === "location";
  // Pickup is a fixed location (see LocationPopover) - pre-populate the
  // field with it instead of a "Location" placeholder the user would
  // otherwise have to open the popover and hit Save on to get to.
  const [locationDisplay, setLocationDisplay] = useState(getPickupAddress);
  const [savedLocationDraft, setSavedLocationDraft] = useState(null);
  const locationFieldRef = useRef();
  const searchBarRef = useRef();
  const isLocationSelected =
    Boolean(locationDisplay) && locationDisplay !== "Location";
  const isFormComplete =
    Boolean(startDate) &&
    Boolean(endDate) &&
    Boolean(startTime) &&
    Boolean(endTime) &&
    isLocationSelected;

  const dateRangeLabel = startDate && endDate
    ? `${formatDisplayDate(startDate)}, ${formatDisplayTime(startTime)} — ${formatDisplayDate(endDate)}, ${formatDisplayTime(endTime)}`
    : startDate
      ? `${formatDisplayDate(startDate)}, ${formatDisplayTime(startTime)} — End date`
      : "Start & end dates";

  /*******************
  @purpose : open the named popover, closing whichever of the other one
             (calendar / location) was open
  @Parameter : {name}
  @Author : INIC
  ******************/
  const togglePopover = (name) => {
    setActivePopover((prev) => (prev === name ? null : name));
  };

  const closePopover = () => setActivePopover(null);

  const handleClickOpenCalendar = () => togglePopover("calendar");
  const handleToggleLocation = () => togglePopover("location");

  /*******************
  @purpose : lock page scroll (and drop .footer-nav below the overlay - see
             body.model-active .footer-nav in _mobile-reservation-search-bar.scss)
             behind whichever full-screen mobile sheet (calendar or
             location) is currently open
  @Author : INIC
  ******************/
  useEffect(() => {
    document.body.classList.toggle("model-active", isShowCalendar || isLocationOpen);
  }, [isShowCalendar, isLocationOpen]);

  /*******************
  @purpose : set start/end date once a range is picked
  @Parameter : {dateObj2}
  @Author : INIC
  ******************/
  const setStartEndDateOnChange = (dateObj2) => {
    if (
      dateObj2 &&
      Object.keys(dateObj2).length === 0 &&
      Object.getPrototypeOf(dateObj2) === Object.prototype
    ) {
      setDateObj(stateObj);
      setStartDate(null);
      setEndDate(null);
    } else {
      setDateObj([dateObj2]);
      setStartDate(format(dateObj2.startDate, "MMM d, yyyy", {}));
      setEndDate(format(dateObj2.endDate, "MMM d, yyyy", {}));
    }
  };

  /*******************
  @purpose : close the location popover on outside click
  @Author : INIC
  ******************/
  useEffect(() => {
    if (!isLocationOpen) return;

    const handleOutsideClick = (e) => {
      // Google Places renders its suggestion dropdown in a `.pac-container`
      // appended directly to <body>, outside the popover's DOM subtree.
      if (e.target.closest && e.target.closest(".pac-container")) return;

      // The mobileSheet popover itself is rendered as a sibling of
      // .mobile-reservation-card now (see below) rather than nested inside
      // locationFieldRef's own subtree - it has to be, since that card has
      // backdrop-filter, which makes it a containing block for
      // position: fixed descendants and was trapping the "full-screen"
      // overlay inside the small card instead of the actual viewport.
      // Clicks inside the sheet's actual content need their own exemption
      // here since locationFieldRef.contains() can no longer see them -
      // scoped to .location-popover (the sheet) rather than
      // .location-popover-overlay (the full-screen backdrop it sits in),
      // so a click on the dimmed backdrop itself still falls through to
      // close it below, instead of being exempted too.
      if (e.target.closest && e.target.closest(".location-popover")) return;

      if (
        locationFieldRef.current &&
        !locationFieldRef.current.contains(e.target)
      ) {
        closePopover();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isLocationOpen]);

  /*******************
  @purpose : Next button handler - persist the picked date/time/location and
             go to the location-wide search results page
  @Author : INIC
  ******************/
  const handleNextClick = () => {
    if (currentLocationId) {
      set_cookie("currentLocationId", currentLocationId, 1);
    }
    set_cookie("daterange", `${startDate || ""}|${endDate || ""}`, 1);
    set_cookie("timerange", `${startTime || ""}|${endTime || ""}`, 1);
    set_cookie("selectedLocationDisplay", locationDisplay || "", 1);
    // Full draft (one-way, delivery/return address), not just the display
    // string - always written (even when unselected, clearing to "") so a
    // stale draft from an earlier visit can't leak into this fresh search.
    set_cookie(
      "selectedLocationDraft",
      savedLocationDraft ? JSON.stringify(savedLocationDraft) : "",
      1
    );

    // Also carried in the URL (not just the cookie) so the search-results
    // page can restore this exact search on back/forward navigation.
    const query = {};
    if (startDate) query.startDate = startDate;
    if (endDate) query.endDate = endDate;
    if (startTime) query.startTime = startTime;
    if (endTime) query.endTime = endTime;
    if (locationDisplay) query.location = locationDisplay;
    if (savedLocationDraft) query.locationDraft = JSON.stringify(savedLocationDraft);

    router.push({ pathname: "/search-results", query });
  };

  return (
    <div className="mobile-reservation-search-bar" ref={searchBarRef}>
      <div className="mobile-reservation-card">
        <div
          className="mobile-reservation-field"
          onClick={handleClickOpenCalendar}
        >
          <IconCalendar className="mobile-reservation-field-icon" />
          <span
            className={`mobile-field-label ${
              startDate ? "mobile-field-label-filled" : "mobile-field-label-placeholder"
            }`}
          >
            {dateRangeLabel}
          </span>
        </div>

        <div
          className="mobile-reservation-field mobile-location-field"
          ref={locationFieldRef}
        >
          <div
            className="mobile-location-field-trigger"
            onClick={handleToggleLocation}
          >
            <IconLocationPin className="mobile-reservation-field-icon" />
            <span
              className={`mobile-field-label ${
                isLocationSelected ? "mobile-field-label-filled" : "mobile-field-label-placeholder"
              }`}
            >
              {locationDisplay || LOCATION_PLACEHOLDER}
            </span>
          </div>
        </div>

        <button
          type="button"
          className="mobile-reservation-next-btn"
          onClick={handleNextClick}
          disabled={!isFormComplete}
        >
          Next <IconArrowRight className="mobile-reservation-next-btn-icon" />
        </button>
      </div>

      {isLocationOpen && (
        <LocationPopover
          initialDraft={savedLocationDraft}
          onClose={closePopover}
          onSave={(display, draft) => {
            setLocationDisplay(display || "Location");
            setSavedLocationDraft(draft);
            closePopover();
          }}
          mobileSheet
        />
      )}

      {isShowCalendar && (
        <DateRangePicker
          dateObj={dateObj}
          setDateObj={setDateObj}
          handleClickOpenCalendar={handleClickOpenCalendar}
          setStartEndDateOnChange={setStartEndDateOnChange}
          startTimeOptionsHandler={() => {}}
          setStartTimeDefaultValue={() => {}}
          setEndTimeDefaultValue={() => {}}
          tempDirections="vertical"
          minimumDay={1}
          showMinimumErrorMessage={() => {}}
          selectedStartDate={startDate}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          isAvailable={false}
          mobileSheet
          startTime={startTime}
          endTime={endTime}
          onStartTimeChange={setStartTime}
          onEndTimeChange={setEndTime}
        />
      )}
    </div>
  );
}

export default MobileReservationSearchBar;
